import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Grid,
  Row,
  Skeleton,
  Statistic,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ReloadOutlined } from "@ant-design/icons";
import { Chart } from "react-google-charts";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ProviderHealthCollection,
  WhatsappProviderAvailability,
  WhatsappProviderHealth,
  WhatsappProviderIncident,
} from "../../interfaces/WhatsappProviderHealth";
import {
  buscarDisponibilidadeProviderWhatsapp,
  buscarHistoricoProviderWhatsapp,
  buscarSaudeProviderWhatsapp,
  verificarProviderWhatsappAgora,
} from "../../services/whatsappProviderHealthService";
import { useMessage } from "../../uteis/mensagens/useMessage";

const REFRESH_INTERVAL_MS = 60_000;
const { useBreakpoint } = Grid;

function formatDate(value?: string | null, includeYear = false) {
  if (!value || Number.isNaN(new Date(value).getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    ...(includeYear ? { year: "numeric" } : {}),
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatMonth(value: string) {
  const [year, month] = value.split("-").map(Number);
  if (!year || !month) {
    return value;
  }

  return new Intl.DateTimeFormat("pt-BR", {
    month: "short",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

function formatDuration(startedAt?: string | null, endedAt?: string | null) {
  if (!startedAt || Number.isNaN(new Date(startedAt).getTime())) {
    return "—";
  }

  const durationMs = Math.max(
    0,
    new Date(endedAt || new Date().toISOString()).getTime() - new Date(startedAt).getTime()
  );
  const totalMinutes = Math.floor(durationMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours} h${minutes ? ` ${minutes} min` : ""}`;
  }

  return `${minutes} min`;
}

function isHealthy(status: string) {
  return status.toLowerCase() === "healthy";
}

function isDegraded(status: string) {
  return status.toLowerCase() === "degraded";
}

function statusLabel(status: string) {
  if (isHealthy(status)) {
    return "Saudável";
  }

  if (isDegraded(status)) {
    return "Degradado";
  }

  return status.toLowerCase() === "offline" ? "Indisponível" : status;
}

function statusColor(status: string) {
  if (isHealthy(status)) {
    return "success";
  }

  return isDegraded(status) ? "warning" : "error";
}

function getCurrentStatus(entries: WhatsappProviderHealth[]) {
  return (
    entries.find((entry) => entry.status.toLowerCase() === "offline") ||
    entries.find((entry) => isDegraded(entry.status)) ||
    entries.find((entry) => !isHealthy(entry.status)) ||
    entries[0]
  );
}

export default function ViewWhatsappProviderHealth() {
  const { showError, showSuccess, contextHolder } = useMessage();
  const screens = useBreakpoint();
  const isDesktop = Boolean(screens.lg);
  const [health, setHealth] = useState<ProviderHealthCollection<WhatsappProviderHealth>>({ items: [] });
  const [history, setHistory] = useState<ProviderHealthCollection<WhatsappProviderIncident>>({ items: [] });
  const [availability, setAvailability] = useState<ProviderHealthCollection<WhatsappProviderAvailability>>({ items: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isCheckingNow, setIsCheckingNow] = useState(false);

  const loadDashboard = useCallback(async (showLoading = false) => {
    if (showLoading) {
      setIsLoading(true);
    }

    try {
      const [nextHealth, nextHistory, nextAvailability] = await Promise.all([
        buscarSaudeProviderWhatsapp(),
        buscarHistoricoProviderWhatsapp(),
        buscarDisponibilidadeProviderWhatsapp(),
      ]);
      setHealth(nextHealth);
      setHistory(nextHistory);
      setAvailability(nextAvailability);
      setError(false);
    } catch (requestError) {
      console.error(requestError);
      setError(true);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadDashboard(true);
    const intervalId = window.setInterval(() => loadDashboard(), REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [loadDashboard]);

  const current = getCurrentStatus(health.items);
  const latestMonth = availability.items[availability.items.length - 1];
  const latestMonthIsCovered = Boolean(
    latestMonth?.covered && latestMonth.availabilityPercent !== null
  );
  const monitoringStartedAt =
    availability.monitoringStartedAt || history.monitoringStartedAt || health.monitoringStartedAt;
  const availabilityChartData = useMemo(
    () => [
      ["Mês", "Disponibilidade"],
      ...availability.items
        .filter(
          (item) => item.covered && item.availabilityPercent !== null
        )
        .map((item) => [formatMonth(item.month), item.availabilityPercent as number]),
    ],
    [availability.items]
  );

  const handleCheckNow = async () => {
    setIsCheckingNow(true);
    try {
      await verificarProviderWhatsappAgora();
      await loadDashboard();
      showSuccess("Verificação do provider concluída.");
    } catch (requestError) {
      console.error(requestError);
      showError("Não foi possível verificar o provider agora.");
    } finally {
      setIsCheckingNow(false);
    }
  };

  const historyColumns: ColumnsType<WhatsappProviderIncident> = [
    {
      title: "Início",
      dataIndex: "startedAt",
      render: (value: string) => formatDate(value),
    },
    {
      title: "Fim",
      dataIndex: "recoveredAt",
      render: (value: string | null) => (value ? formatDate(value) : "Em aberto"),
    },
    {
      title: "Duração",
      render: (_, incident) => formatDuration(incident.startedAt, incident.recoveredAt),
    },
    {
      title: "Situação",
      dataIndex: "lastStatus",
      render: (value: string, incident) => (
        <Badge
          status={incident.recoveredAt ? "success" : statusColor(value)}
          text={incident.recoveredAt ? "Recuperado" : statusLabel(value)}
        />
      ),
    },
    {
      title: "Erro",
      ellipsis: true,
      render: (_, incident) =>
        [incident.statusCode ? `HTTP ${incident.statusCode}` : null, incident.detail]
          .filter(Boolean)
          .join(" — ") || "—",
    },
  ];

  const pageStyle = {
    boxSizing: "border-box" as const,
    width: "100%",
    maxWidth: isDesktop ? 1440 : 1200,
    padding: isDesktop ? "40px 48px 64px" : "24px 16px 40px",
  };

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <Skeleton active paragraph={{ rows: 14 }} />
      </div>
    );
  }

  return (
    <div role="main" style={pageStyle}>
      {contextHolder}
      <Card
        bordered={false}
        style={{ marginBottom: 24, borderRadius: 16 }}
        styles={{ body: { padding: isDesktop ? "28px 32px" : 20 } }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col flex="auto">
          <Typography.Title level={2} style={{ marginBottom: 4 }}>
            Saúde do WhatsApp
          </Typography.Title>
          <Typography.Text type="secondary">
            Acompanhe a disponibilidade do provider oficial e os incidentes registrados.
          </Typography.Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={isCheckingNow}
            onClick={handleCheckNow}
          >
            Verificar agora
          </Button>
        </Col>
        </Row>
      </Card>

      {error ? (
        <Alert
          type="warning"
          showIcon
          message="Não foi possível atualizar todos os dados do monitoramento."
          description="Tente novamente em instantes. Os últimos dados carregados continuam visíveis quando disponíveis."
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {!current ? (
        <Alert
          type="info"
          showIcon
          message="Nenhum provider monitorado foi retornado."
          description="O histórico e a disponibilidade serão exibidos assim que o monitoramento estiver ativo."
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[32, 16]} align="middle">
            <Col xs={24} lg={18}>
              <Typography.Text type="secondary">Status atual</Typography.Text>
              <Typography.Title level={3} style={{ margin: "4px 0" }}>
                <Badge status={statusColor(current.status)} text={`${current.provider} ${statusLabel(current.status)}`} />
              </Typography.Title>
              <Typography.Text>
                Canal {current.channelId} · última verificação: {formatDate(current.checkedAt, true)}
              </Typography.Text>
              {!isHealthy(current.status) && current.incidentStartedAt ? (
                <Typography.Paragraph type="danger" style={{ margin: "8px 0 0" }}>
                  {isDegraded(current.status) ? "Degradado" : "Indisponível"} há {formatDuration(current.incidentStartedAt)}
                  {current.statusCode ? ` · HTTP ${current.statusCode}` : ""}
                  {current.detail ? ` · ${current.detail}` : ""}
                </Typography.Paragraph>
              ) : null}
            </Col>
            {current.latencyMs !== null && current.latencyMs !== undefined ? (
              <Col>
                <Statistic title="Latência" value={current.latencyMs} suffix="ms" />
              </Col>
            ) : null}
          </Row>
        </Card>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Disponibilidade no último mês"
              value={latestMonthIsCovered ? latestMonth?.availabilityPercent as number : "Sem monitoramento"}
              precision={latestMonthIsCovered ? 2 : undefined}
              suffix={latestMonthIsCovered ? "%" : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Minutos indisponíveis"
              value={latestMonthIsCovered ? latestMonth?.unavailableMinutes as number : "Sem monitoramento"}
              suffix={latestMonthIsCovered ? "min" : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card><Statistic title="Incidentes no último mês" value={latestMonthIsCovered ? latestMonth?.incidents as number : "Sem monitoramento"} /></Card>
        </Col>
      </Row>

      <Card title="Disponibilidade mensal" style={{ marginBottom: 16 }}>
        {availabilityChartData.length > 1 ? (
          <Chart
            chartType="LineChart"
            width="100%"
            height={isDesktop ? "380px" : "320px"}
            data={availabilityChartData}
            options={{
              legend: { position: "none" },
              vAxis: { minValue: 0, maxValue: 100, format: "#'%'" },
              colors: ["#2f855a"],
              chartArea: { width: isDesktop ? "88%" : "82%", height: "70%" },
              pointSize: 5,
            }}
          />
        ) : (
          <Empty description="Sem monitoramento para o período." />
        )}
        {monitoringStartedAt ? (
          <Typography.Text type="secondary">
            Cobertura do monitoramento desde {formatDate(monitoringStartedAt, true)}.
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            Sem monitoramento: ainda não houve uma checagem real do provider.
          </Typography.Text>
        )}
      </Card>

      <Card title="Histórico de incidentes">
        <Table<WhatsappProviderIncident>
          columns={historyColumns}
          dataSource={history.items}
          rowKey="id"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: "Nenhum incidente registrado." }}
          scroll={{ x: 760 }}
        />
      </Card>
    </div>
  );
}
